/**
 * NoteTemplateBuilder — admin UI to create + edit note templates.
 *
 * Split into two panes: a compact list on the left ("Templates") and
 * an editor on the right for the currently-selected template.
 *
 * Design notes:
 * - Sections/fields have stable client-generated UUIDs so re-ordering
 *   and conditional references remain valid across saves.
 * - "Save" persists as draft; "Publish" flips ``published=true`` and
 *   bumps the version so new notes snapshot the fresh structure.
 * - Conditional logic keeps it simple: pick a "controller" field and
 *   the value(s) that make this field visible. Only one controller
 *   per field for now (server-side AND-across-keys is future work).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Plus, ChevronDown, ChevronUp, Trash2, Save, Send, FileText,
  ArrowUp, ArrowDown, GripVertical, Loader2, Archive, Copy,
  Check, X, AlertTriangle, Search,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const FIELD_TYPES = [
  ["text",        "Short text"],
  ["textarea",    "Long text"],
  ["number",      "Number"],
  ["select",      "Single select"],
  ["multiselect", "Multi select"],
  ["radio",       "Radio group"],
  ["checkbox",    "Checkbox"],
  ["date",        "Date"],
  ["body_chart",  "Body chart"],
  ["outcome_measure", "Outcome measure"],
];

const uid = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

function newField(overrides = {}) {
  return {
    id: uid(),
    name: `field_${uid().slice(0, 4)}`,
    label: "New field",
    help: "",
    type: "text",
    required: false,
    options: [],
    measure_id: null,
    conditional: null,
    order: 0,
    ...overrides,
  };
}

function newSection() {
  return {
    id: uid(),
    name: "New section",
    description: "",
    collapsed_by_default: false,
    fields: [],
  };
}


export default function NoteTemplateBuilder() {
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [measureRegistry, setMeasureRegistry] = useState([]);
  const [apptTypes, setApptTypes] = useState([]);
  const [apptTypesError, setApptTypesError] = useState("");

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [t, m] = await Promise.all([
        api.get("/note-templates", { params: { include_archived: true } }),
        api.get("/outcome-measures"),
      ]);
      setTemplates(t.data?.templates || []);
      setMeasureRegistry(m.data?.measures || []);
    } catch (e) {
      toast.error("Failed to load templates", { description: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
    // Appointment types are pulled live from Acuity — failure is soft: the
    // picker collapses to a manual "paste IDs" fallback so the builder still
    // works when Acuity is offline / not configured.
    try {
      const r = await api.get("/appointment-types");
      const rows = Array.isArray(r.data) ? r.data : (r.data?.items || []);
      setApptTypes(rows);
      setApptTypesError("");
    } catch (e) {
      setApptTypes([]);
      setApptTypesError(e?.response?.data?.detail || "Acuity appointment types unavailable — paste IDs manually below.");
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    // When the selected id changes, deep-clone the row so edits are local
    // until Save.
    const src = templates.find((t) => t.id === selectedId);
    setSelected(src ? JSON.parse(JSON.stringify(src)) : null);
  }, [selectedId, templates]);

  async function createNew() {
    try {
      const r = await api.post("/note-templates", {
        name: "Untitled template",
        description: "",
        sections: [newSection()],
      });
      await refresh();
      setSelectedId(r.data.id);
      toast.success("Template created — start editing.");
    } catch (e) {
      toast.error("Create failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  async function save() {
    if (!selected) return;
    setSaving(true);
    try {
      await api.patch(`/note-templates/${selected.id}`, {
        name: selected.name,
        description: selected.description,
        kind: selected.kind || "generic",
        appointment_type_ids: selected.appointment_type_ids || [],
        sections: selected.sections,
      });
      toast.success("Saved as draft — publish to make live.");
      await refresh();
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  }

  async function publish() {
    if (!selected) return;
    // Save first so the published version reflects the latest edits.
    await save();
    try {
      await api.post(`/note-templates/${selected.id}/publish`);
      toast.success("Template published — new notes now use this version.");
      await refresh();
    } catch (e) {
      toast.error("Publish failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  async function archive() {
    if (!selected) return;
    if (!window.confirm(`Archive "${selected.name}"? Existing notes are untouched — but this template stops appearing on the "New note" picker.`)) return;
    try {
      await api.delete(`/note-templates/${selected.id}`);
      toast.success("Template archived");
      setSelectedId(null);
      await refresh();
    } catch (e) {
      toast.error("Archive failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  async function duplicate() {
    if (!selected) return;
    try {
      const r = await api.post(`/note-templates/${selected.id}/duplicate`);
      toast.success(`Duplicated as "${r.data?.name}" — edit and publish when ready.`);
      await refresh();
      setSelectedId(r.data?.id);
    } catch (e) {
      toast.error("Duplicate failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  // -- Local mutators -----------------------------------------------------
  function patchTop(patch) { setSelected((t) => ({ ...t, ...patch })); }
  function patchSection(sid, patch) {
    setSelected((t) => ({
      ...t,
      sections: t.sections.map((s) => (s.id === sid ? { ...s, ...patch } : s)),
    }));
  }
  function moveSection(sid, dir) {
    setSelected((t) => {
      const idx = t.sections.findIndex((s) => s.id === sid);
      const next = [...t.sections];
      const swap = idx + dir;
      if (swap < 0 || swap >= next.length) return t;
      [next[idx], next[swap]] = [next[swap], next[idx]];
      return { ...t, sections: next };
    });
  }
  function removeSection(sid) {
    setSelected((t) => ({ ...t, sections: t.sections.filter((s) => s.id !== sid) }));
  }
  function addSection() {
    setSelected((t) => ({ ...t, sections: [...(t.sections || []), newSection()] }));
  }
  function addField(sid) {
    setSelected((t) => ({
      ...t,
      sections: t.sections.map((s) =>
        s.id === sid ? { ...s, fields: [...s.fields, newField({ order: s.fields.length })] } : s
      ),
    }));
  }
  function patchField(sid, fid, patch) {
    setSelected((t) => ({
      ...t,
      sections: t.sections.map((s) =>
        s.id !== sid ? s : {
          ...s,
          fields: s.fields.map((f) => (f.id === fid ? { ...f, ...patch } : f)),
        }
      ),
    }));
  }
  function moveField(sid, fid, dir) {
    setSelected((t) => ({
      ...t,
      sections: t.sections.map((s) => {
        if (s.id !== sid) return s;
        const idx = s.fields.findIndex((f) => f.id === fid);
        const swap = idx + dir;
        if (swap < 0 || swap >= s.fields.length) return s;
        const fields = [...s.fields];
        [fields[idx], fields[swap]] = [fields[swap], fields[idx]];
        return { ...s, fields: fields.map((f, i) => ({ ...f, order: i })) };
      }),
    }));
  }
  function removeField(sid, fid) {
    setSelected((t) => ({
      ...t,
      sections: t.sections.map((s) => (s.id === sid ? { ...s, fields: s.fields.filter((f) => f.id !== fid) } : s)),
    }));
  }

  // All fields available across sections — used as options for conditional
  // controllers in the field editor.
  const allFields = (selected?.sections || []).flatMap((s) => s.fields.map((f) => ({ ...f, _section: s.name })));

  // Which other (non-archived) templates already claim each appointment type?
  // Used for the soft-warning surface on the picker — overlap is allowed
  // (auto-pick just resolves to the first match), but the user should see
  // when they're introducing a conflict.
  const linksByType = useMemo(() => {
    const m = new Map();
    for (const t of templates) {
      if (t.archived) continue;
      if (selected && t.id === selected.id) continue;
      for (const id of (t.appointment_type_ids || [])) {
        const key = String(id);
        if (!m.has(key)) m.set(key, []);
        m.get(key).push({ id: t.id, name: t.name });
      }
    }
    return m;
  }, [templates, selected]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4 min-h-[560px]" data-testid="note-template-builder">
      {/* Templates list */}
      <Card className="p-3 rounded-lg border border-zinc-200 shadow-none h-fit">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">Templates</div>
          <Button size="sm" variant="outline" onClick={createNew} data-testid="ntb-new-template">
            <Plus className="h-3.5 w-3.5 mr-1" /> New
          </Button>
        </div>
        {loading ? (
          <div className="text-xs text-zinc-500 flex items-center gap-2 p-2">
            <Loader2 className="h-3 w-3 animate-spin" /> Loading…
          </div>
        ) : templates.length === 0 ? (
          <div className="text-xs text-zinc-500 italic p-2">No templates yet. Click New to start.</div>
        ) : (
          <ul className="space-y-1">
            {templates.map((t) => (
              <li key={t.id}>
                <button
                  className={cn(
                    "w-full text-left px-2 py-1.5 rounded-md border text-sm transition-colors",
                    selectedId === t.id
                      ? "bg-zinc-950 text-white border-zinc-950"
                      : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400",
                    t.archived && "opacity-60",
                  )}
                  onClick={() => setSelectedId(t.id)}
                  data-testid={`ntb-template-${t.id}`}
                >
                  <div className="font-medium truncate flex items-center gap-1.5">
                    <FileText className="h-3 w-3 shrink-0" />{t.name}
                  </div>
                  <div className={cn("text-2xs", selectedId === t.id ? "text-zinc-300" : "text-zinc-500")}>
                    v{t.version} · {t.published ? "published" : "draft"}
                    {t.archived && " · archived"}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* Editor */}
      <Card className="p-4 rounded-lg border border-zinc-200 shadow-none">
        {!selected ? (
          <div className="text-sm text-zinc-500 italic p-6 text-center">
            Pick a template on the left, or click &quot;New&quot; to start.
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-[220px]">
                <Input
                  value={selected.name}
                  onChange={(e) => patchTop({ name: e.target.value })}
                  className="text-lg font-semibold border-0 border-b border-zinc-200 rounded-none px-0 focus-visible:ring-0 focus-visible:border-zinc-950"
                  data-testid="ntb-template-name"
                />
                <Textarea
                  value={selected.description || ""}
                  onChange={(e) => patchTop({ description: e.target.value })}
                  placeholder="Short description — when should this template be used?"
                  rows={2}
                  className="mt-2"
                  data-testid="ntb-template-description"
                />
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-zinc-500">Type:</span>
                    {[
                      { v: "initial", l: "Initial" },
                      { v: "follow_up", l: "Follow-up" },
                      { v: "reassessment", l: "Reassessment" },
                      { v: "generic", l: "Generic" },
                    ].map((k) => (
                      <button
                        key={k.v}
                        type="button"
                        onClick={() => patchTop({ kind: k.v })}
                        className={
                          "text-2xs px-2 py-0.5 rounded border transition " +
                          ((selected.kind || "generic") === k.v
                            ? "bg-blue-600 border-blue-700 text-white"
                            : "bg-white border-zinc-300 text-zinc-700 hover:bg-zinc-50")
                        }
                        data-testid={`ntb-kind-${k.v}`}
                      >
                        {k.l}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="mt-3">
                  <Label className="text-xs text-zinc-500 block mb-1">
                    Auto-select on these appointment types
                    <span className="text-zinc-400"> (optional — leave blank for a generic template)</span>
                  </Label>
                  <AppointmentTypeSelector
                    apptTypes={apptTypes}
                    apptTypesError={apptTypesError}
                    linksByType={linksByType}
                    selectedIds={selected.appointment_type_ids || []}
                    onChange={(next) => patchTop({ appointment_type_ids: next })}
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={save} disabled={saving} data-testid="ntb-save-btn">
                  <Save className="h-3.5 w-3.5 mr-1.5" /> Save draft
                </Button>
                <Button onClick={publish} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="ntb-publish-btn">
                  <Send className="h-3.5 w-3.5 mr-1.5" /> Publish
                </Button>
                <Button variant="outline" onClick={duplicate} disabled={saving} data-testid="ntb-duplicate-btn">
                  <Copy className="h-3.5 w-3.5 mr-1.5" /> Duplicate
                </Button>
                <Button variant="outline" onClick={archive} className="border-red-300 text-red-700 hover:bg-red-50" data-testid="ntb-archive-btn">
                  <Archive className="h-3.5 w-3.5 mr-1.5" /> Archive
                </Button>
              </div>
            </div>

            <div className="text-2xs text-zinc-500">
              Version {selected.version} · {selected.published ? "published (edits become draft)" : "draft — unpublished"}
            </div>

            {selected.sections.map((s, sIdx) => (
              <SectionEditor
                key={s.id}
                section={s}
                allFields={allFields}
                measureRegistry={measureRegistry}
                isFirst={sIdx === 0}
                isLast={sIdx === selected.sections.length - 1}
                onPatch={(p) => patchSection(s.id, p)}
                onMove={(dir) => moveSection(s.id, dir)}
                onRemove={() => removeSection(s.id)}
                onAddField={() => addField(s.id)}
                onPatchField={(fid, p) => patchField(s.id, fid, p)}
                onMoveField={(fid, dir) => moveField(s.id, fid, dir)}
                onRemoveField={(fid) => removeField(s.id, fid)}
              />
            ))}

            <Button variant="outline" onClick={addSection} data-testid="ntb-add-section">
              <Plus className="h-3.5 w-3.5 mr-1.5" /> Add section
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}


// ---------------------------------------------------------------------------
// SectionEditor
// ---------------------------------------------------------------------------

function SectionEditor({
  section, allFields, measureRegistry,
  isFirst, isLast, onPatch, onMove, onRemove, onAddField,
  onPatchField, onMoveField, onRemoveField,
}) {
  const [collapsed, setCollapsed] = useState(false);
  return (
    <div className="border border-zinc-200 rounded-md" data-testid={`ntb-section-${section.id}`}>
      <div className="flex items-center gap-2 p-2 border-b border-zinc-200 bg-zinc-50">
        <button onClick={() => setCollapsed(!collapsed)} className="text-zinc-500">
          {collapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
        </button>
        <Input
          value={section.name}
          onChange={(e) => onPatch({ name: e.target.value })}
          className="h-8 font-medium max-w-xs"
          data-testid={`ntb-section-name-${section.id}`}
        />
        <div className="ml-auto flex items-center gap-1">
          <button onClick={() => onMove(-1)} disabled={isFirst} className={cn("p-1 rounded", isFirst ? "opacity-30" : "hover:bg-zinc-200")}>
            <ArrowUp className="h-3.5 w-3.5" />
          </button>
          <button onClick={() => onMove(1)} disabled={isLast} className={cn("p-1 rounded", isLast ? "opacity-30" : "hover:bg-zinc-200")}>
            <ArrowDown className="h-3.5 w-3.5" />
          </button>
          <button onClick={onRemove} className="p-1 rounded hover:bg-red-100 text-red-600">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      {!collapsed && (
        <div className="p-3 space-y-2">
          <Textarea
            value={section.description || ""}
            onChange={(e) => onPatch({ description: e.target.value })}
            placeholder="Section description (optional)"
            rows={1}
          />
          {section.fields.length === 0 ? (
            <div className="text-xs text-zinc-500 italic border border-dashed border-zinc-200 rounded p-3">
              No fields yet. Click &quot;Add field&quot; below.
            </div>
          ) : (
            <ul className="space-y-2">
              {section.fields.map((f, i) => (
                <FieldEditor
                  key={f.id}
                  field={f}
                  isFirst={i === 0}
                  isLast={i === section.fields.length - 1}
                  otherFields={allFields.filter((o) => o.id !== f.id)}
                  measureRegistry={measureRegistry}
                  onPatch={(p) => onPatchField(f.id, p)}
                  onMove={(dir) => onMoveField(f.id, dir)}
                  onRemove={() => onRemoveField(f.id)}
                />
              ))}
            </ul>
          )}
          <Button size="sm" variant="outline" onClick={onAddField} data-testid={`ntb-add-field-${section.id}`}>
            <Plus className="h-3.5 w-3.5 mr-1.5" /> Add field
          </Button>
        </div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// FieldEditor
// ---------------------------------------------------------------------------

function FieldEditor({ field, isFirst, isLast, otherFields, measureRegistry, onPatch, onMove, onRemove }) {
  const [expanded, setExpanded] = useState(false);
  const controllerField = field.conditional?.show_if
    ? Object.keys(field.conditional.show_if)[0] || ""
    : "";
  const controllerAllowed = controllerField ? field.conditional.show_if[controllerField] : "";
  const controllerFieldObj = otherFields.find((o) => o.id === controllerField);

  return (
    <li className="border border-zinc-200 rounded p-2 bg-white" data-testid={`ntb-field-${field.id}`}>
      <div className="flex items-center gap-2">
        <GripVertical className="h-4 w-4 text-zinc-300" />
        <Input value={field.label} onChange={(e) => onPatch({ label: e.target.value })} placeholder="Label" className="h-8" />
        <select
          value={field.type}
          onChange={(e) => onPatch({ type: e.target.value })}
          className="h-8 px-2 border border-zinc-300 rounded text-sm bg-white min-w-[130px]"
          data-testid={`ntb-field-type-${field.id}`}
        >
          {FIELD_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <label className="text-xs flex items-center gap-1">
          <input
            type="checkbox" checked={!!field.required}
            onChange={(e) => onPatch({ required: e.target.checked })}
            data-testid={`ntb-field-required-${field.id}`}
          /> req
        </label>
        <div className="ml-auto flex items-center gap-1">
          <button onClick={() => setExpanded(!expanded)} className="text-2xs uppercase text-zinc-500 hover:text-zinc-900 tracking-wider">
            {expanded ? "less" : "more"}
          </button>
          <button onClick={() => onMove(-1)} disabled={isFirst} className={cn("p-1 rounded", isFirst ? "opacity-30" : "hover:bg-zinc-100")}>
            <ArrowUp className="h-3.5 w-3.5" />
          </button>
          <button onClick={() => onMove(1)} disabled={isLast} className={cn("p-1 rounded", isLast ? "opacity-30" : "hover:bg-zinc-100")}>
            <ArrowDown className="h-3.5 w-3.5" />
          </button>
          <button onClick={onRemove} className="p-1 rounded hover:bg-red-50 text-red-600">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      {expanded && (
        <div className="mt-2 pl-6 pr-1 space-y-2 border-t border-zinc-100 pt-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-2xs uppercase tracking-wider text-zinc-500">Key (programmatic)</Label>
              <Input value={field.name} onChange={(e) => onPatch({ name: e.target.value })} placeholder="pain_now" className="h-8" />
            </div>
            <div>
              <Label className="text-2xs uppercase tracking-wider text-zinc-500">Help text</Label>
              <Input value={field.help || ""} onChange={(e) => onPatch({ help: e.target.value })} placeholder="Optional hint" className="h-8" />
            </div>
          </div>
          {(field.type === "select" || field.type === "multiselect" || field.type === "radio") && (
            <div>
              <Label className="text-2xs uppercase tracking-wider text-zinc-500">Options (one per line)</Label>
              <Textarea
                rows={3}
                value={(field.options || []).join("\n")}
                onChange={(e) => onPatch({ options: e.target.value.split("\n").map((o) => o.trim()).filter(Boolean) })}
                placeholder="Yes&#10;No&#10;Not sure"
              />
            </div>
          )}
          {field.type === "outcome_measure" && (
            <div>
              <Label className="text-2xs uppercase tracking-wider text-zinc-500">Which measure?</Label>
              <select
                value={field.measure_id || ""}
                onChange={(e) => onPatch({ measure_id: e.target.value || null })}
                className="h-8 px-2 border border-zinc-300 rounded text-sm bg-white w-full"
              >
                <option value="">(pick a measure)</option>
                {measureRegistry.map((m) => <option key={m.id} value={m.id}>{m.name} · {m.unit}</option>)}
              </select>
            </div>
          )}
          {/* Phase-2 AI hints — how this field should be filled from
              a transcript. Optional; without these the model just
              falls back to generic SOAP output. */}
          <div className="border-t border-zinc-100 pt-2 space-y-2 bg-indigo-50/40 -mx-2 px-2 pb-2 rounded">
            <div className="text-2xs uppercase tracking-wider text-indigo-800 font-semibold flex items-center gap-1">
              AI hints (Phase 2)
            </div>
            <div>
              <Label className="text-2xs uppercase tracking-wider text-zinc-500">Prompt to LLM</Label>
              <Textarea
                rows={2}
                value={field.prompt || ""}
                onChange={(e) => onPatch({ prompt: e.target.value })}
                placeholder="e.g. Summarise today's ROM findings, compare to baseline if mentioned."
                className="text-xs"
                data-testid={`ntb-field-prompt-${field.id}`}
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-2xs uppercase tracking-wider text-zinc-500">Preferred source</Label>
                <select
                  value={field.preferred_source || ""}
                  onChange={(e) => onPatch({ preferred_source: e.target.value || null })}
                  className="h-8 px-2 border border-zinc-300 rounded text-sm bg-white w-full"
                  data-testid={`ntb-field-preferred-source-${field.id}`}
                >
                  <option value="">(any)</option>
                  <option value="subjective">Subjective</option>
                  <option value="objective">Objective</option>
                  <option value="assessment">Assessment</option>
                  <option value="plan">Plan</option>
                </select>
              </div>
              <div>
                <Label className="text-2xs uppercase tracking-wider text-zinc-500">Output rule</Label>
                <select
                  value={field.output_rule || ""}
                  onChange={(e) => onPatch({ output_rule: e.target.value || null })}
                  className="h-8 px-2 border border-zinc-300 rounded text-sm bg-white w-full"
                  data-testid={`ntb-field-output-rule-${field.id}`}
                >
                  <option value="">(auto)</option>
                  <option value="bullet">Bullet list</option>
                  <option value="paragraph">Paragraph</option>
                  <option value="measurement">Measurement</option>
                  <option value="code_list">Code list</option>
                </select>
              </div>
            </div>
          </div>
          <div className="border-t border-zinc-100 pt-2">
            <Label className="text-2xs uppercase tracking-wider text-zinc-500">Conditional — show only when</Label>
            <div className="flex gap-2 items-center mt-1">
              <select
                value={controllerField}
                onChange={(e) => {
                  const cid = e.target.value;
                  if (!cid) return onPatch({ conditional: null });
                  onPatch({ conditional: { show_if: { [cid]: "" } } });
                }}
                className="h-8 px-2 border border-zinc-300 rounded text-sm bg-white flex-1"
                data-testid={`ntb-field-cond-controller-${field.id}`}
              >
                <option value="">(always visible)</option>
                {otherFields.map((o) => (
                  <option key={o.id} value={o.id}>{o.label || o.name} ({o._section})</option>
                ))}
              </select>
              {controllerField && (
                <>
                  <span className="text-xs text-zinc-500">equals</span>
                  {controllerFieldObj?.options?.length ? (
                    <select
                      value={Array.isArray(controllerAllowed) ? controllerAllowed[0] || "" : controllerAllowed || ""}
                      onChange={(e) => onPatch({ conditional: { show_if: { [controllerField]: e.target.value } } })}
                      className="h-8 px-2 border border-zinc-300 rounded text-sm bg-white"
                    >
                      <option value="">(pick a value)</option>
                      {controllerFieldObj.options.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : (
                    <Input
                      value={Array.isArray(controllerAllowed) ? controllerAllowed[0] || "" : controllerAllowed || ""}
                      onChange={(e) => onPatch({ conditional: { show_if: { [controllerField]: e.target.value } } })}
                      className="h-8 w-40"
                      placeholder="value"
                    />
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </li>
  );
}



// ---------------------------------------------------------------------------
// AppointmentTypeSelector
// Multi-select picker for Acuity appointment types.
// - Popover + search + checkbox list; entries show name · duration · category
// - Selected types render above the picker as removable chips
// - Soft warning badge when a picked type is already claimed by another
//   published/draft template (overlap is allowed but auto-pick will resolve
//   to the first match, so the user should know).
// - Graceful fallback: if Acuity is unreachable/not-configured, we surface
//   the error text and drop back to a comma-separated ID input so the
//   builder still works.
// ---------------------------------------------------------------------------
function AppointmentTypeSelector({
  apptTypes, apptTypesError, linksByType, selectedIds, onChange,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  // Normalise everything to strings; Acuity IDs come as numbers but the
  // db can store either, so compare via String() throughout.
  const selectedSet = useMemo(() => new Set((selectedIds || []).map(String)), [selectedIds]);

  const byId = useMemo(() => {
    const m = new Map();
    for (const t of apptTypes || []) m.set(String(t.id), t);
    return m;
  }, [apptTypes]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const list = (apptTypes || []).filter((t) => t.active !== false);
    if (!needle) return list;
    return list.filter((t) => {
      const hay = `${t.name || ""} ${t.category || ""} ${t.id}`.toLowerCase();
      return hay.includes(needle);
    });
  }, [apptTypes, q]);

  function toggle(idStr) {
    const next = new Set(selectedSet);
    if (next.has(idStr)) next.delete(idStr);
    else next.add(idStr);
    onChange(Array.from(next));
  }

  function removeOne(idStr) {
    onChange((selectedIds || []).map(String).filter((x) => x !== idStr));
  }

  function selectAllFiltered() {
    const next = new Set(selectedSet);
    for (const t of filtered) next.add(String(t.id));
    onChange(Array.from(next));
  }

  function clearAll() {
    onChange([]);
  }

  // Fallback UI when Acuity isn't reachable: comma-separated IDs.
  if (apptTypesError && (!apptTypes || apptTypes.length === 0)) {
    return (
      <div className="space-y-1">
        <div className="text-2xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 flex items-start gap-1.5">
          <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
          <span>{apptTypesError}</span>
        </div>
        <Input
          value={(selectedIds || []).join(", ")}
          onChange={(e) => onChange(
            e.target.value.split(",").map((s) => s.trim()).filter(Boolean)
          )}
          placeholder="e.g. 42184827, 42184828"
          className="text-xs h-8"
          data-testid="ntb-appointment-type-ids-fallback"
        />
      </div>
    );
  }

  // Selected chips + warnings (rendered above the picker).
  const conflicts = (selectedIds || [])
    .map(String)
    .flatMap((id) => (linksByType.get(id) || []).map((t) => ({ typeId: id, ...t })));

  return (
    <div className="space-y-2" data-testid="ntb-appointment-type-selector">
      {selectedIds && selectedIds.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="ntb-appointment-type-chips">
          {selectedIds.map((rawId) => {
            const id = String(rawId);
            const t = byId.get(id);
            const label = t ? t.name : `ID ${id}`;
            const meta = t ? [t.duration ? `${t.duration}m` : null, t.category].filter(Boolean).join(" · ") : "unknown type";
            const others = linksByType.get(id) || [];
            return (
              <Badge
                key={id}
                variant="secondary"
                className={cn(
                  "gap-1 pl-2 pr-1 py-0.5 font-normal border",
                  others.length
                    ? "bg-amber-50 border-amber-300 text-amber-900"
                    : "bg-zinc-100 border-zinc-200 text-zinc-800",
                )}
                data-testid={`ntb-appt-chip-${id}`}
                title={others.length ? `Also linked to: ${others.map((o) => o.name).join(", ")}` : meta}
              >
                <span className="max-w-[220px] truncate">{label}</span>
                {meta && <span className="text-2xs text-zinc-500 hidden sm:inline">· {meta}</span>}
                <button
                  type="button"
                  onClick={() => removeOne(id)}
                  className="ml-0.5 p-0.5 rounded hover:bg-zinc-200/70"
                  aria-label={`Remove ${label}`}
                  data-testid={`ntb-appt-chip-remove-${id}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            );
          })}
        </div>
      )}

      {conflicts.length > 0 && (
        <div
          className="text-2xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 flex items-start gap-1.5"
          data-testid="ntb-appt-conflicts"
        >
          <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
          <span>
            {conflicts.length === 1
              ? `1 type is already linked to another template — auto-pick will resolve to whichever template Mongo returns first.`
              : `${conflicts.length} types are already linked to other templates — auto-pick may prefer another template.`}
            {" "}
            <span className="text-amber-900">
              {Array.from(new Set(conflicts.map((c) => c.name))).slice(0, 3).join(", ")}
              {new Set(conflicts.map((c) => c.name)).size > 3 && "…"}
            </span>
          </span>
        </div>
      )}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 text-xs"
            data-testid="ntb-appt-picker-trigger"
          >
            <Plus className="h-3.5 w-3.5 mr-1" />
            {selectedIds && selectedIds.length > 0
              ? `Edit appointment types (${selectedIds.length})`
              : "Pick appointment types"}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-[420px] p-0"
          data-testid="ntb-appt-picker-content"
        >
          <div className="p-2 border-b border-zinc-200 flex items-center gap-2">
            <Search className="h-3.5 w-3.5 text-zinc-400 ml-1" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, category, or ID…"
              className="h-7 flex-1 text-sm outline-none bg-transparent"
              data-testid="ntb-appt-picker-search"
              autoFocus
            />
          </div>
          <div className="max-h-[280px] overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-xs text-zinc-500 italic text-center">
                {q ? "No matches" : "No appointment types found on Acuity."}
              </div>
            ) : (
              filtered.map((t) => {
                const id = String(t.id);
                const checked = selectedSet.has(id);
                const others = linksByType.get(id) || [];
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => toggle(id)}
                    className={cn(
                      "w-full text-left px-2.5 py-1.5 flex items-start gap-2 hover:bg-zinc-50 border-l-2",
                      checked ? "border-emerald-500 bg-emerald-50/40" : "border-transparent",
                    )}
                    data-testid={`ntb-appt-option-${id}`}
                  >
                    <span
                      className={cn(
                        "mt-0.5 h-4 w-4 rounded border flex items-center justify-center shrink-0",
                        checked ? "bg-emerald-600 border-emerald-600 text-white" : "bg-white border-zinc-300",
                      )}
                    >
                      {checked && <Check className="h-3 w-3" />}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-zinc-900 truncate flex items-center gap-1.5">
                        {t.name}
                        {others.length > 0 && (
                          <span
                            className="inline-flex items-center gap-0.5 text-2xs font-normal text-amber-800 bg-amber-100 border border-amber-200 rounded px-1"
                            title={`Also on: ${others.map((o) => o.name).join(", ")}`}
                          >
                            <AlertTriangle className="h-2.5 w-2.5" />
                            on {others.length === 1 ? others[0].name : `${others.length} templates`}
                          </span>
                        )}
                      </div>
                      <div className="text-2xs text-zinc-500 truncate">
                        {[t.duration ? `${t.duration} min` : null, t.category, `#${id}`]
                          .filter(Boolean)
                          .join(" · ")}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
          <div className="border-t border-zinc-200 p-1.5 flex items-center justify-between text-2xs">
            <div className="text-zinc-500 px-1">
              {selectedSet.size} selected · {filtered.length} shown
            </div>
            <div className="flex gap-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-2xs"
                onClick={selectAllFiltered}
                disabled={filtered.length === 0}
                data-testid="ntb-appt-select-all"
              >
                Select filtered
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-2xs text-red-700 hover:bg-red-50"
                onClick={clearAll}
                disabled={selectedSet.size === 0}
                data-testid="ntb-appt-clear-all"
              >
                Clear all
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
